import { IdCard } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import {
  useDataTable,
  type DataTablePage,
  type DataTableQuery,
} from '../../../../components/data-table/useDataTable';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateTime } from '../../../../lib/formatDate';
import { ApiError } from '../../../../lib/apiError';
import { plantillaApi } from '../../plantilla/plantillaApi';
import { salaryTypeLabel, type PlantillaPositionListItem } from '../../plantilla/plantillaTypes';
import { PositionModal } from '../components/PositionModal';
import { positionsApi } from '../positionsApi';
import type { PositionListItem } from '../positionTypes';
import { RichTextDocument } from '../../../../components/RichTextDocument';

type TabKey = 'info' | 'qualifications' | 'posts';

/**
 * One catalog title, on the **header** profile layout.
 *
 * A title reads as one continuous thing — a designation, a grade, what it demands, and the
 * posts drawn from it — so it takes the header shape rather than the sidebar one.
 */
export function PositionProfilePage() {
  const { id = '' } = useParams<{ id: string }>();

  /*
    The post count is counted from the staffing pattern, so it is another module's grant —
    the same reason the index gates its Posts column and its Usage facet. Everything else on
    this page is the catalog's own and needs only `positions.read`.
  */
  const canReadPlantilla = usePermission('plantilla.read');

  const [position, setPosition] = useState<PositionListItem | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [tab, setTab] = useState<TabKey>('info');
  const [editing, setEditing] = useState(false);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    positionsApi
      .get(id, controller.signal)
      .then((loaded) => {
        setPosition(loaded);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(
          caught instanceof ApiError ? caught : new ApiError('The position could not be loaded.', 0),
        );
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  /*
    The posts are a table of records inside a profile, so they go through <DataTablePanel> —
    search, sort, resize and paging — rather than a hand-rolled list. Server-side throughout:
    the endpoint is scoped to this title with `positionId`, which is a scope rather than a
    facet because the surface sets it and the reader cannot.
  */
  const fetchPosts = useCallback(
    async (query: DataTableQuery, signal: AbortSignal): Promise<DataTablePage<PlantillaPositionListItem>> => {
      const page = await plantillaApi.list(
        {
          positionId: id,
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
        },
        signal,
      );

      return { items: page.items, total: page.total };
    },
    [id],
  );

  const postsTable = useDataTable<PlantillaPositionListItem>({ fetchPage: fetchPosts, defaultSort: 'code' });

  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    { key: 'qualifications', label: 'Qualification standards' },
    {
      key: 'posts',
      label: (
        <>
          Posts
          <CountBadge count={position?.postCount ?? 0} tone="slate" />
        </>
      ),
      // The panel reads the staffing pattern, which is another module's grant entirely.
      permission: 'plantilla.read',
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Human Resources',
          'Organization',
          { label: 'Settings', to: '/organization/settings' },
          { label: 'Positions', to: '/organization/settings/positions' },
          position ? position.title : '',
        ]}
      />

      {loadError ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !position ? (
        <div className="flex flex-col gap-3 p-6">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full max-w-3xl" />
          ))}
        </div>
      ) : (
        <ProfileHeaderShell<TabKey>
          id="position-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          panel={tab === 'posts' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="position-profile-header"
              icon={<IdCard className="h-5 w-5" />}
              title={position.title}
              // The grade is the Info card's — the card can name the salary type outright.
              meta={[position.code]}
              status={
                position.isActive ? (
                  <StatusText tone="active">Active</StatusText>
                ) : (
                  <StatusText tone="inactive">Deleted</StatusText>
                )
              }
              createdBy={position.createdByName}
              createdAt={position.createdAt}
            />
          }
          banner={
            <ProfileBanner
              id="position-profile-banner"
              tone={position.isActive ? 'emerald' : 'slate'}
              message={
                !position.isActive
                  ? 'This position is deleted. It is no longer offered when a post is authorized; the posts already drawn from it keep the title they were authorized under.'
                  : // The count is the staffing pattern's, so a caller without that grant gets
                    // the state without it rather than no banner at all.
                    canReadPlantilla
                    ? position.postCount === 0
                      ? 'Offered when a post is authorized. No post is drawn from it yet.'
                      : `${position.postCount} post${position.postCount === 1 ? '' : 's'} in the staffing pattern ${position.postCount === 1 ? 'is' : 'are'} drawn from this title.`
                    : 'Offered when a post is authorized.'
              }
            />
          }
        >
          {tab === 'info' ? (
            <>
              <ProfileCard
                id="position-profile-basic"
                title="Basic Info"
                onEdit={() => setEditing(true)}
                editPermission="positions.update"
              >
                <PreviewTable variant="table-display" rows={basicRows(position, canReadPlantilla)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id="position-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(position)} />
              </ProfileCard>
            </>
          ) : tab === 'qualifications' ? (
            <>
              {/*
                One card per standard, each editing through the same modal — the whole QS is
                one section of the form, so splitting the edit four ways would open the same
                dialog from four places to change one field each.
              */}
              <ProfileCard
                id="position-profile-eligibility"
                title="Eligibility"
                onEdit={() => setEditing(true)}
                editPermission="positions.update"
              >
                <PreviewTable
                  variant="table-display"
                  rows={[{ label: 'Required', value: position.eligibilityName }]}
                />
              </ProfileCard>

              <ProfileCard
                padded
                id="position-profile-education"
                title="Education"
                onEdit={() => setEditing(true)}
                editPermission="positions.update"
              >
                <RichTextDocument html={position.education} />
              </ProfileCard>

              <ProfileCard
                padded
                id="position-profile-training"
                title="Training"
                onEdit={() => setEditing(true)}
                editPermission="positions.update"
              >
                <RichTextDocument html={position.training} />
              </ProfileCard>

              <ProfileCard
                padded
                id="position-profile-experience"
                title="Experience"
                onEdit={() => setEditing(true)}
                editPermission="positions.update"
              >
                <RichTextDocument html={position.experience} />
              </ProfileCard>
            </>
          ) : (
            /*
              Full height, so the pager sits on the bottom edge of the tab rather than
              floating up under a short table.
            */
            <div className="flex h-full min-w-0 flex-col p-4">
              <DataTablePanel
                id="position-posts"
                state={postsTable}
                columns={POST_COLUMNS}
                rowKey={(post) => post.id}
                searchPlaceholder="Search posts or item numbers"
                emptyMessage="No post in the staffing pattern is drawn from this title yet."
                emptySearchMessage="No post here matches that search."
                fill
              />
            </div>
          )}
        </ProfileHeaderShell>
      )}

      {editing && position ? (
        <PositionModal
          open
          editing={position}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * What the header does not already say.
 *
 * The title and the code are the header's — repeating them here would be the same value in
 * two slots. The grade lives here rather than in the header line because the card can state
 * the salary type outright, where the compact label leaves General implicit.
 */
function basicRows(position: PositionListItem, canReadPlantilla: boolean): PreviewRow[] {
  return [
    { label: 'Salary grade', value: position.salaryGradeLabel },
    // Always present, both of them: the grade is required, so neither row can be an em dash.
    { label: 'Type', value: salaryTypeLabel(position.salaryGradeType) },
    // Dropped rather than dashed for a caller without the staffing pattern: an em dash would
    // say the title has no posts, which is a different fact from not being allowed to know.
    ...(canReadPlantilla
      ? [{ label: 'Posts', value: <span className="font-mono">{position.postCount}</span> }]
      : []),
    { label: 'Description', value: position.description },
  ];
}

/**
 * The rest of the trail.
 *
 * Created-by and created-at are the header's on this layout, on screen for every tab — so
 * this card carries only what the header does not, which is the update half.
 */
function auditRows(position: PositionListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: position.updatedByName },
    {
      label: 'Updated',
      value: position.updatedAt ? (
        <span className="font-mono">{formatDateTime(position.updatedAt)}</span>
      ) : null,
    },
  ];
}

/** The posts drawn from this title. The title itself is not a column — every row shares it. */
const POST_COLUMNS: DataColumn<PlantillaPositionListItem>[] = [
  {
    key: 'code',
    header: 'Code',
    width: 140,
    sortable: true,
    render: (post) => <TextCell value={post.code} mono />,
  },
  {
    key: 'orgUnitName',
    header: 'Unit',
    width: 240,
    sortable: true,
    render: (post) => <TextCell value={post.orgUnitName} />,
  },
  {
    key: 'salaryGrade',
    header: 'Grade',
    width: 140,
    sortable: true,
    render: (post) => <TextCell value={post.salaryGradeLabel} />,
  },
  {
    key: 'authorizedSlots',
    header: 'Authorized',
    width: 110,
    align: 'right',
    render: (post) => <TextCell value={String(post.authorizedSlots)} mono numeric />,
  },
  {
    key: 'vacantSlots',
    header: 'Vacant',
    width: 100,
    align: 'right',
    render: (post) => <TextCell value={String(post.vacantSlots)} mono numeric />,
  },
  {
    /*
      `status: true` places it at the end of the DATA columns and is what the list view reads
      for the row's right-hand badge. Declared last it looked correct in the table and showed
      no state at all in the list — the flag is the fact, the position is a consequence of it.
    */
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (post) =>
      post.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
